import { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2";
import generateRandomKey from "../../views/components/GenerateRandomKey";
import { staticUrl } from "../../config";
import OptimisedImage from "../../views/components/OptimisedImage";
import useResponsiveSlides from "../../lib/useResponsiveSlides";

const BREAKPOINTS = [
  { breakpoint: 1280, settings: { slidesToShow: 2, slidesToScroll: 1 } },
  { breakpoint: 800, settings: { slidesToShow: 1, slidesToScroll: 1 } },
];

const ProductsCarousel = ({ products, colors }) => {
  let baseSlidesToShow = 1;

  if (products.length > 3) {
    baseSlidesToShow = 3;
  } else if (products.length > 2) {
    baseSlidesToShow = 2;
  } else if (products.length > 1) {
    baseSlidesToShow = 1;
  }

  // slidesToShow drives the page layout (by product count); the slider's
  // actual count also depends on the viewport width.
  const slidesToShow = baseSlidesToShow;
  const { ready: isMounted, slidesToShow: sliderSlidesToShow } =
    useResponsiveSlides(
      { slidesToShow: baseSlidesToShow, slidesToScroll: 1 },
      BREAKPOINTS
    );

  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleCount, setVisibleCount] = useState(slidesToShow);

  const updateVisibleCount = () => {
    if (sliderRef.current?.innerSlider?.state) {
      setVisibleCount(sliderRef.current.innerSlider.state.slidesToShow);
    }
  };

  // Slider is keyed on its slide count and remounts at slide 0 when it changes
  useEffect(() => {
    setCurrentSlide(0);
    setVisibleCount(sliderSlidesToShow);
  }, [sliderSlidesToShow]);

  const NextArrow = (props) => {
    const { onClick } = props;
    return (
      <button
        aria-label="Right"
        className="text-kb-400/30 hover:text-kb-400 text-2xl xl:text-5xl focus:outline-none absolute -top-12 bottom-0 -right-10 xl:-right-16 z-10 mt-auto mb-auto"
        onClick={onClick}
      >
        <HiChevronRight />
      </button>
    );
  };

  const PrevArrow = (props) => {
    const { onClick } = props;
    return (
      <button
        aria-label="Left"
        className="text-kb-400/30 hover:text-kb-400 text-2xl xl:text-5xl focus:outline-none absolute -top-12 bottom-0 -left-10 xl:-left-16 z-10 mt-auto mb-auto"
        onClick={onClick}
      >
        <HiChevronLeft />
      </button>
    );
  };

  const settings = {
    dots: false,
    fade: false,
    infinite: true,
    draggable: false,
    speed: 500,
    slidesToShow: sliderSlidesToShow,
    slidesToScroll: 1,
    autoplay: false,
    autoplaySpeed: 8000,
    waitForAnimate: true,
    className: "relative", // Add relative positioning to the parent container
    cssEase: "linear",
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    afterChange: (index) => {
      setCurrentSlide(index);
      updateVisibleCount();
    },
    onReInit: updateVisibleCount,
    onInit: updateVisibleCount,
  };

  const total = products?.length || 0;
  const visibleIndices = new Set(
    Array.from({ length: visibleCount }, (_, j) => (currentSlide + j) % total)
  );

  let maxWidth = "1600px";

  if (slidesToShow === 1) {
    maxWidth = "540px";
  } else if (slidesToShow === 2) {
    maxWidth = "1080px";
  } else if (slidesToShow >= 3) {
    maxWidth = "1640px";
  }

  return (
    <>
      {slidesToShow === 1 ? (
        <div className={`max-w-[${maxWidth}] px-12 mx-auto`}>
          <div className="px-5">
            <div className="h-[20px]"></div>
            <a href={`/products/${products[0]?.slug}`}>
              <div
                className="bg-white relative"
                style={{ boxShadow: "0 0 25px rgba(112, 128, 144, 0.30)" }}
              >
                {products[0]?.is_new ? (
                  <div className="absolute top-6 right-6 bg-kb-400 text-white py-1 px-3 rounded items-center text-sm uppercase font-medium">
                    New
                  </div>
                ) : (
                  <></>
                )}

                {products[0]?.relation_id ? (
                  <OptimisedImage
                    src={`${staticUrl}/img/media/products/product_${products[0]?.relation_id}.jpg`}
                    height="500"
                    width="500"
                    alt="Product Image"
                  />
                ) : (
                  <OptimisedImage
                    src={`${staticUrl}/img/noimage.jpg`}
                    height="500"
                    width="500"
                    alt="No Image Available"
                  />
                )}

                {colors && (
                  <div className="flex items-center space-x-3 p-6 pt-5">
                    <div className="h-[32px] -ml-3"></div>
                    {colors[`${products[0]?.product_id}`]
                      .slice(0, 3)
                      .map((color, index) => (
                        <div
                          key={index}
                          className="w-[32px] h-[32px] bg-gray-200"
                        >
                          <OptimisedImage
                            src={`${staticUrl}/img/media/colors/thumbnail_${color}.jpg`}
                            height="32"
                            width="32"
                            alt="Color Thumbnail"
                          />
                        </div>
                      ))}

                    {colors[`${products[0]?.product_id}`].length > 3 && (
                      <span className="text-kb-400/70">+ More</span>
                    )}
                  </div>
                )}
              </div>
            </a>
            <div className="flex w-full justify-center py-6">
              <div className="text-center">
                <div className="text-gray-600">{products[0]?.product_name}</div>
                <div className="text-kb-300 font-bold uppercase">
                  {products[0]?.collection_name}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={`max-w-[${maxWidth}] px-16 xl:px-24 mx-auto overflow-x-hidden`}>
          {isMounted && (
            <Slider key={sliderSlidesToShow} ref={sliderRef} {...settings}>
              {products?.map((product, index) => (
                <div key={generateRandomKey()} className="px-5">
                  <div className="h-[20px]"></div>
                  <a href={`/products/${product.slug}`} tabIndex={visibleIndices.has(index) ? 0 : -1}>
                    <div
                      className="bg-white relative"
                      style={{
                        boxShadow: "0 0 25px rgba(112, 128, 144, 0.30)",
                      }}
                    >
                      {product.is_new ? (
                        <div className="absolute top-6 right-6 bg-kb-400 text-white py-1 px-3 rounded items-center text-sm uppercase font-medium">
                          New
                        </div>
                      ) : (
                        <></>
                      )}

                      {product.relation_id ? (
                        <OptimisedImage
                          src={`${staticUrl}/img/media/products/medium_${product.relation_id}.jpg`}
                          fallbackSrc={`${staticUrl}/img/media/products/product_${product.relation_id}.jpg`}
                          height="600"
                          width="600"
                          alt="Product Image"
                        />
                      ) : (
                        <OptimisedImage
                          src={`${staticUrl}/img/noimage.jpg`}
                          height="600"
                          width="600"
                          alt="No Image Available"
                        />
                      )}

                      {colors && (
                        <div className="flex items-center space-x-3 p-6 pt-5">
                          <div className="h-[32px] -ml-3"></div>
                          {colors[`${product.product_id}`]
                            .slice(0, 3)
                            .map((color, index) => (
                              <div
                                key={index}
                                className="w-[32px] h-[32px] bg-gray-200"
                              >
                                <OptimisedImage
                                  src={`${staticUrl}/img/media/colors/thumbnail_${color}.jpg`}
                                  height="32"
                                  width="32"
                                  alt="Color Thumbnail"
                                />
                              </div>
                            ))}

                          {colors[`${product.product_id}`].length > 3 && (
                            <span className="text-kb-400/70">+ More</span>
                          )}
                        </div>
                      )}
                    </div>
                  </a>
                  <div className="flex w-full justify-center py-6">
                    <div className="text-center">
                      <div className="text-gray-600">{product.product_name}</div>
                      <div className="text-kb-300 font-bold uppercase">
                        {product.collection_name}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          )}
        </div>
      )}
    </>
  );
};

export default ProductsCarousel;
